import React, { useState, useEffect } from "react";
import i18n from "i18next";

// import QrReader from "react-qr-reader";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import queryString from "query-string";

// Fluent UI Imports
import { baseUrl } from "../../helpers/constants";
import useWindowSize from "../../helpers/useWindowSize";
import { AccountDataProtectionDto } from "../../Models/AccountDataProtection";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Image,
  Input,
  Label,
  MessageBar,
  MessageBarActions,
  TeachingPopover,
  TeachingPopoverBody,
  TeachingPopoverFooter,
  TeachingPopoverHeader,
  TeachingPopoverSurface,
  TeachingPopoverTitle,
  TeachingPopoverTrigger,
  Text,
  Textarea,
} from "@fluentui/react-components";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import { InfoRegular } from "@fluentui/react-icons";

interface IAddUserProductDialogProps {
  userProducts: any;
  isDialogOpen: boolean;
  onProductAdded(): void;
  onDismiss(): void;
  isToolbox: boolean;
  isPartnerLounge: boolean;
  location: any;
}

function AddUserProductDialog(props: IAddUserProductDialogProps) {
  const value = queryString.parse(props?.location?.search);
  const systemId = value.systemId?.toString();
  const auftragId = value.auftragId?.toString();
  const artikelId = value.artikelId?.toString();
  const produktId = value.productId?.toString();

  const windowSize = useWindowSize();

  const [addUserProductValues, setAddUserProductValues] = useState({
    SystemId: "",
    AuftragId: "",
    ArtikelId: "",
    ProduktId: "",
    UserProductText: "",
    UserProductNumber: "",
    IsToolbox: props.isToolbox,
    IsPartnerLounge: props.isPartnerLounge,
  });
  const handleAddUserProductValuesChange = (name: string, val: any) => {
    setAddUserProductValues((prevState) => ({
      ...prevState,
      [name]: val,
    }));
  };

  const [addUserProductError, setAddUserProductError] = useState("");
  const [hasSystemId, setHasSystemId] = useState<boolean>(false);
  const [isValidProduktId, setIsValidProduktId] = useState<boolean>(false);
  const [isValidArtikelId, setIsValidArtikelId] = useState<boolean>(false);

  useEffect(() => {
    setAddUserProductError("");
    setAddUserProductValues({
      SystemId: systemId ? systemId : "", // System-ID
      AuftragId: auftragId ? auftragId : "", // Auftrag
      ArtikelId: artikelId ? artikelId : "", // Artikelnummer
      ProduktId: produktId ? produktId : "", // Seriennummer
      UserProductText: "",
      UserProductNumber: "",
      IsToolbox: props.isToolbox,
      IsPartnerLounge: props.isPartnerLounge,
    });
    setHasSystemId(false);
    setIsValidProduktId(false);
    setIsValidArtikelId(false);
    setRequestMessage("");
    getDataProtectionSettings();
  }, [props.isDialogOpen]);

  const addUserProduct = () => {
    fetchWrapper
      .postWithAuth(`${baseUrl}/api/UserProduct/Add`, addUserProductValues)
      .then((result: any) => {
        props.onProductAdded();
      })
      .catch((error) => {
        setAddUserProductError(error);
      });
  };

  useEffect(() => {
    if (
      addUserProductValues.ArtikelId.length == 8 &&
      addUserProductValues.ProduktId.length >= 6
    ) {
      checkIfProductHasSystemId(
        addUserProductValues.ArtikelId,
        addUserProductValues.ProduktId
      );
    }
  }, [addUserProductValues]);

  useEffect(() => {
    if (
      addUserProductValues.ProduktId &&
      addUserProductValues.ProduktId.length >= 6
    ) {
      checkIfValidProduktId(addUserProductValues.ProduktId);
    }
  }, [addUserProductValues.ProduktId]);

  useEffect(() => {
    if (
      addUserProductValues.ArtikelId &&
      addUserProductValues.ArtikelId.length == 8
    ) {
      checkIfValidArtikelId(addUserProductValues.ArtikelId);
    }
  }, [addUserProductValues.ArtikelId]);

  const checkIfValidProduktId = (produktId: string) => {
    if (produktId) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/checkIfValidProduktId?produktId=${produktId}`
        )
        .then((result: boolean) => {
          setIsValidProduktId(result);
        })
        .catch(() => {
          setIsValidProduktId(false);
        });
    }
  };

  const checkIfValidArtikelId = (artikelId: string) => {
    if (artikelId) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/checkIfValidArtikelId?artikelId=${artikelId}`
        )
        .then((result: boolean) => {
          setIsValidArtikelId(result);
        })
        .catch(() => {
          setIsValidArtikelId(false);
        });
    }
  };

  const checkIfProductHasSystemId = (artikelId: string, produktId: string) => {
    if (artikelId && produktId) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/checkIfProductHasSystemId?artikelId=${artikelId}&produktId=${produktId}`
        )
        .then((result: boolean) => {
          setHasSystemId(result);
        })
        .catch(() => {
          setHasSystemId(false);
        });
    }
  };

  const requestMigration = () => {
    if (addUserProductValues.IsToolbox == true) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/RequestOvertakeProduct?systemId=${addUserProductValues.SystemId}&artikelId=${addUserProductValues.ArtikelId}&productId=${addUserProductValues.ProduktId}&area=Toolbox`
        )
        .then((message) => {
          setAddUserProductError("");
          setRequestMessage(message);
        })
        .catch((error) => {
          setAddUserProductError(error);
        });
    } else if (addUserProductValues.IsPartnerLounge == true) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/UserProduct/RequestOvertakeProduct?systemId=${addUserProductValues.SystemId}&auftragId=${addUserProductValues.AuftragId}&area=Partner`
        )
        .then((result) => {
          setAddUserProductError("");
          setRequestMessage(result.message);
        })
        .catch((error) => {
          setAddUserProductError(error);
        });
    }
  };

  const [showRequestButton, setShowRequestButton] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");

  const canSave = () => {
    if (addUserProductValues.IsPartnerLounge == true) {
      if (
        addUserProductValues.AuftragId.length > 0 &&
        addUserProductValues.SystemId.length > 0
      ) {
        return true;
      }
    } else {
      if (
        hasSystemId &&
        addUserProductValues.SystemId.length > 0 &&
        isValidProduktId &&
        isValidArtikelId
      ) {
        return true;
      } else if (!hasSystemId && isValidProduktId && isValidArtikelId) {
        return true;
      }
    }
    return false;
  };

  const getDataProtectionSettings = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/GetDataProtectionSettings`)
      .then((result: AccountDataProtectionDto) => {
        if (result.dataSecAllowFindByEmailDomain == true) {
          setShowRequestButton(true);
        } else {
          setShowRequestButton(false);
        }
      });
  };

  return (
    <div>
      <Dialog
        open={props.isDialogOpen}
        onOpenChange={(ev, data) => {
          if (!data.open) {
            props.onDismiss();
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{i18n.t<string>("addUserProduct")}</DialogTitle>
            <DialogContent>
              <Text>
                {props.isPartnerLounge
                  ? i18n.t<string>("addUserProductPartnerText")
                  : i18n.t<string>("addUserProductText")}
              </Text>

              {props.isPartnerLounge && (
                <div>
                  <StackShim
                    horizontal
                    verticalAlign="center"
                    horizontalAlign="stretch"
                  >
                    <Label>{i18n.t<string>("auftragId")}</Label>

                    <TeachingPopover>
                      <TeachingPopoverTrigger>
                        <TeachingPopoverTrigger>
                          <Button
                            appearance="transparent"
                            icon={<InfoRegular />}
                          />
                        </TeachingPopoverTrigger>
                      </TeachingPopoverTrigger>
                      <TeachingPopoverSurface>
                        <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                        <TeachingPopoverBody
                          media={
                            <Image
                              alt="MUSTERRECHNUNG_AUFTRAGSNUMMER"
                              fit="cover"
                              src={`${baseUrl}/IMAGES/HELPIMAGES/MUSTERRECHNUNG_AUFTRAGSNUMMER.JPG`}
                            />
                          }
                        >
                          <TeachingPopoverTitle>
                            {i18n.t<string>("auftragId")}
                          </TeachingPopoverTitle>
                          <div>
                            {i18n.t<string>("addUserProductAuftragIdHint")}
                          </div>
                        </TeachingPopoverBody>
                        <TeachingPopoverFooter
                          primary={i18n.t<string>("close")}
                        />
                      </TeachingPopoverSurface>
                    </TeachingPopover>
                  </StackShim>

                  <div style={{ width: "100%" }}>
                    <Input
                      name="auftragId"
                      value={addUserProductValues.AuftragId}
                      onChange={(ev, data) => {
                        handleAddUserProductValuesChange(
                          "AuftragId",
                          data.value
                        );
                      }}
                    />
                  </div>
                </div>
              )}

              {props.isToolbox && (
                <div>
                  <StackShim horizontal verticalAlign="center">
                    <Label>{i18n.t<string>("artikelId") + " (P/N)"}</Label>

                    <TeachingPopover>
                      <TeachingPopoverTrigger>
                        <TeachingPopoverTrigger>
                          <Button
                            appearance="transparent"
                            icon={<InfoRegular />}
                          />
                        </TeachingPopoverTrigger>
                      </TeachingPopoverTrigger>
                      <TeachingPopoverSurface>
                        <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                        <TeachingPopoverBody
                          media={
                            <Image
                              alt="PRODUKTLASERBESCHRIFTUNG_PN"
                              fit="cover"
                              src={`${baseUrl}/IMAGES/HELPIMAGES/PRODUKTLASERBESCHRIFTUNG_PN.JPG`}
                            />
                          }
                        >
                          <TeachingPopoverTitle>
                            {i18n.t<string>("artikelId")}
                          </TeachingPopoverTitle>
                          <div>
                            {i18n.t<string>("addUserProductArtikelIdHint")}
                          </div>
                        </TeachingPopoverBody>
                        <TeachingPopoverFooter
                          primary={i18n.t<string>("close")}
                        />
                      </TeachingPopoverSurface>
                    </TeachingPopover>
                  </StackShim>

                  <div style={{ width: "100%" }}>
                    <Input
                      value={addUserProductValues.ArtikelId}
                      onChange={(ev, data) => {
                        handleAddUserProductValuesChange(
                          "ArtikelId",
                          data.value.replaceAll(" ", "")
                        );
                      }}
                    />
                  </div>

                  {!isValidArtikelId && (
                    <MessageBar intent="error">
                      {i18n.t<string>("enterValidArtikelId")}
                    </MessageBar>
                  )}
                </div>
              )}

              {props.isToolbox && (
                <div>
                  <StackShim horizontal verticalAlign="center">
                    <Label>{i18n.t<string>("productId") + " (S/N)"}</Label>

                    <TeachingPopover>
                      <TeachingPopoverTrigger>
                        <TeachingPopoverTrigger>
                          <Button
                            appearance="transparent"
                            icon={<InfoRegular />}
                          />
                        </TeachingPopoverTrigger>
                      </TeachingPopoverTrigger>
                      <TeachingPopoverSurface>
                        <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                        <TeachingPopoverBody
                          media={
                            <Image
                              alt="PRODUKTLASERBESCHRIFTUNG_SN"
                              fit="cover"
                              src={`${baseUrl}/IMAGES/HELPIMAGES/PRODUKTLASERBESCHRIFTUNG_SN.JPG`}
                            />
                          }
                        >
                          <TeachingPopoverTitle>
                            {i18n.t<string>("productId")}
                          </TeachingPopoverTitle>
                          <div>
                            {i18n.t<string>("addUserProductProductIdHint")}
                          </div>
                        </TeachingPopoverBody>
                        <TeachingPopoverFooter
                          primary={i18n.t<string>("close")}
                        />
                      </TeachingPopoverSurface>
                    </TeachingPopover>
                  </StackShim>
                  <div style={{ width: "100%" }}>
                    <Input
                      value={addUserProductValues.ProduktId}
                      onChange={(ev, data) => {
                        handleAddUserProductValuesChange(
                          "ProduktId",
                          data.value.replaceAll(" ", "")
                        );
                      }}
                    />
                  </div>

                  {!isValidProduktId && (
                    <MessageBar intent="error">
                      {i18n.t<string>("enterValidProduktId")}
                    </MessageBar>
                  )}
                </div>
              )}

              {(hasSystemId || props.isPartnerLounge) && (
                <StackShim horizontal verticalAlign="center">
                  <Label>{i18n.t<string>("systemId") + " (ID)"}</Label>

                  <TeachingPopover>
                    <TeachingPopoverTrigger>
                      <TeachingPopoverTrigger>
                        <Button
                          appearance="transparent"
                          icon={<InfoRegular />}
                        />
                      </TeachingPopoverTrigger>
                    </TeachingPopoverTrigger>
                    <TeachingPopoverSurface>
                      <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                      <TeachingPopoverBody
                        media={
                          <Image
                            alt="PRODUKTLASERBESCHRIFTUNG_ID"
                            fit="cover"
                            src={`${baseUrl}/IMAGES/HELPIMAGES/PRODUKTLASERBESCHRIFTUNG_ID.JPG`}
                          />
                        }
                      >
                        <TeachingPopoverTitle>
                          {i18n.t<string>("systemId")}
                        </TeachingPopoverTitle>
                        <div>
                          {i18n.t<string>("addUserProductSystemIdHint")}
                        </div>
                      </TeachingPopoverBody>
                      <TeachingPopoverFooter
                        primary={i18n.t<string>("close")}
                      />
                    </TeachingPopoverSurface>
                  </TeachingPopover>
                </StackShim>
              )}

              {(hasSystemId || props.isPartnerLounge) && (
                <div style={{ width: "100%" }}>
                  <Input
                    name="systemId"
                    value={addUserProductValues.SystemId}
                    onChange={(ev, data) => {
                      handleAddUserProductValuesChange(
                        "SystemId",
                        data.value.replaceAll(" ", "")
                      );
                    }}
                  />
                </div>
              )}

              {hasSystemId && (
                <div style={{ marginTop: 10 }}>
                  <MessageBar intent="info">
                    {i18n.t<string>("systemIdNeededForThisProduct")}
                  </MessageBar>
                </div>
              )}

              <StackShim horizontal verticalAlign="center">
                <Label>{i18n.t<string>("userProductText")}</Label>

                <TeachingPopover>
                  <TeachingPopoverTrigger>
                    <TeachingPopoverTrigger>
                      <Button appearance="transparent" icon={<InfoRegular />} />
                    </TeachingPopoverTrigger>
                  </TeachingPopoverTrigger>
                  <TeachingPopoverSurface>
                    <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                    <TeachingPopoverBody>
                      <TeachingPopoverTitle>
                        {i18n.t<string>("userProductText")}
                      </TeachingPopoverTitle>
                      <div>
                        {props.isToolbox
                          ? i18n.t<string>(
                              "addUserProductMyProductDescriptionHint"
                            )
                          : i18n.t<string>(
                              "addUserProductMyProductDescriptionHintPartner"
                            )}
                      </div>
                    </TeachingPopoverBody>
                    <TeachingPopoverFooter primary={i18n.t<string>("close")} />
                  </TeachingPopoverSurface>
                </TeachingPopover>
              </StackShim>

              <div style={{ width: "100%" }}>
                <Textarea
                  rows={4}
                  value={addUserProductValues.UserProductText}
                  onChange={(ev, data) => {
                    handleAddUserProductValuesChange(
                      "UserProductText",
                      data.value
                    );
                  }}
                />
              </div>

              <StackShim horizontal verticalAlign="center">
                <Label>{i18n.t<string>("userProductNumber")}</Label>

                <TeachingPopover>
                  <TeachingPopoverTrigger>
                    <TeachingPopoverTrigger>
                      <Button appearance="transparent" icon={<InfoRegular />} />
                    </TeachingPopoverTrigger>
                  </TeachingPopoverTrigger>
                  <TeachingPopoverSurface>
                    <TeachingPopoverHeader>Tipps</TeachingPopoverHeader>
                    <TeachingPopoverBody>
                      <TeachingPopoverTitle>
                        {i18n.t<string>("userProductNumber")}
                      </TeachingPopoverTitle>
                      <div>
                        {i18n.t<string>("addUserProductMyInventoryNumberHint")}
                      </div>
                    </TeachingPopoverBody>
                    <TeachingPopoverFooter primary={i18n.t<string>("close")} />
                  </TeachingPopoverSurface>
                </TeachingPopover>
              </StackShim>

              <div style={{ width: "100%" }}>
                <Input
                  value={addUserProductValues.UserProductNumber}
                  onChange={(ev, data) => {
                    handleAddUserProductValuesChange(
                      "UserProductNumber",
                      data.value
                    );
                  }}
                />
              </div>

              {addUserProductError != "" &&
                addUserProductError != "SYSTEM_ID_ALREADY_REGISTERED" && (
                  <MessageBar className="lueb-MarginTop10" intent="error">
                    {i18n.t<string>(addUserProductError)}
                  </MessageBar>
                )}

              {addUserProductError != "" &&
                addUserProductError == "SYSTEM_ID_ALREADY_REGISTERED" &&
                showRequestButton && (
                  <MessageBar className="lueb-MarginTop10" intent={"error"}>
                    {i18n.t<string>(addUserProductError)}
                    {props.userProducts.some((obj: any) =>
                      props.isPartnerLounge
                        ? obj.seriennummer == addUserProductValues.SystemId
                        : obj.seriennummer == addUserProductValues.ProduktId
                    ) == false && (
                      <MessageBarActions
                        containerAction={
                          <Button
                            aria-label="dismiss"
                            appearance="transparent"
                            onClick={() => requestMigration()}
                          />
                        }
                      ></MessageBarActions>
                    )}
                  </MessageBar>
                )}

              {addUserProductError != "" &&
                addUserProductError == "SYSTEM_ID_ALREADY_REGISTERED" &&
                !showRequestButton && (
                  <MessageBar className="lueb-MarginTop10" intent={"error"}>
                    {i18n.t<string>(addUserProductError)}
                  </MessageBar>
                )}

              {requestMessage == "MIGRATION_REQUEST_CREATED" && (
                <MessageBar className="lueb-MarginTop10" intent={"success"}>
                  {i18n.t<string>("migrationRequestCreated")}
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => props.onDismiss()}>
                {i18n.t<string>("cancel")}
              </Button>

              <Button
                disabled={!canSave()}
                appearance="primary"
                onClick={() => {
                  addUserProduct();
                }}
              >
                {i18n.t<string>("addUserProduct")}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* <Dialog
        minWidth={windowSize.width > 550 ? 500 : windowSize.width - 20}
        modalProps={modalProps}
        hidden={!showQrCodeDialog}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: i18n.t<string>("scanQrCode"),
        }}
      >
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <QrReader
            onScan={(data: string | null) => {
              if (data) {
                const parsedUrl = url.parse(data);
                if (parsedUrl && parsedUrl.query) {
                  const value = queryString.parse(parsedUrl.query);
                  const systemId = value.systemId?.toString();
                  const auftragId = value.auftragId?.toString();
                  const artikelId = value.artikelId?.toString();
                  const productId = value.productId?.toString();

                  setAddUserProductValues({
                    SystemId: systemId
                      ? systemId
                      : addUserProductValues.SystemId,
                    AuftragId: auftragId
                      ? auftragId
                      : addUserProductValues.AuftragId,
                    ArtikelId: artikelId
                      ? artikelId
                      : addUserProductValues.ArtikelId,
                    ProduktId: productId
                      ? productId
                      : addUserProductValues.ProduktId,
                    UserProductNumber: addUserProductValues.UserProductNumber,
                    UserProductText: addUserProductValues.UserProductText,
                    IsPartnerLounge: addUserProductValues.IsPartnerLounge,
                    IsToolbox: addUserProductValues.IsToolbox,
                  });
                  setShowQrCodeDialog(false);
                }
              }
            }}
            onError={(err: any) => {}}
          />
        </div>
        <DialogFooter>
          <DefaultButton
            iconProps={{ iconName: "Cancel" }}
            onClick={() => setShowQrCodeDialog(false)}
            text={i18n.t<string>("cancel")}
          />
        </DialogFooter>
      </Dialog> */}
    </div>
  );
}

export default AddUserProductDialog;
